import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { combineLatest, of, Subject } from 'rxjs';
import { first, map, take, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

import { ApplicationService, DesigneeInfo, RoleRightsInfo, SchoolInfo } from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { SettingsService } from 'services/settings/settings.service';
import { SplashService } from 'services/splash/splash.service';
import { UserInfo, UserService } from 'services/user/user.service';
import { DateFnsPipe } from 'pipes/datefns.pipe';
import { TitleCasePipe } from 'pipes/titlecase.pipe';

@Component({
    selector: 'app-manage-designees',
    styleUrls: ['designees.component.scss'],
    templateUrl: 'designees.component.html',
})

export class ManageDesigneesComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AllHeaders: string[] = ['FirstName', 'LastName', 'SAPID', 'Title', 'ExpirationDate', 'DesigneeActive', 'Rights', 'Actions'];
    AvailableRights: RoleRightsInfo[];
    AvailableSchools: SchoolInfo[];
    AwardInfo: AwardProgramInfo;
    CurrentSchool: SchoolInfo;
    CurrentSchoolYear: string;
    Designees: DesigneeInfo[];
    NoData: boolean;
    ViewReady: boolean;
    ShowingDesigneePanel: boolean;
    DesigneesData: MatTableDataSource<DesigneeInfo>;
    DesigneeEntryForm: FormGroup;
    User: UserInfo;
    maxDate: Date;

    constructor(
        private appService: ApplicationService,
        private awardService: AwardsService,
        private dateFnsPipe: DateFnsPipe,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private settingsSvc: SettingsService,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AvailableRights = null;
        self.AvailableSchools = null;
        self.AwardInfo = null;
        self.CurrentSchool = null;
        self.CurrentSchoolYear = null;
        self.Designees = null;
        self.NoData = false;
        self.User = null;
        self.ViewReady = false;
        self.ShowingDesigneePanel = false;
        self.DesigneesData = new MatTableDataSource<DesigneeInfo>([]);

        this.maxDate = moment().add(3, 'y').toDate();

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
				self.User = _user;
				if (_user == null) return;
            });

			// Load static data for use in the compoment.
			const curYearObs = self.settingsSvc.CurrentSchoolYearSubject;
            const curAwardObs = self.awardService.CurrentAwardsSub;
            const curSchoolObs = self.settingsSvc.CurrentSchoolSubject;

            combineLatest(curYearObs, curAwardObs, curSchoolObs).pipe(
                takeUntil(self._subrelease),
                map(packet => {
                    return {
                        CurrentYear: packet[0],
                        CurrentAward: packet[1],
                        CurrentSchool: packet[2],
                    };
                })
            ).subscribe(packet => {
                self.CurrentSchoolYear = packet.CurrentYear;
                self.AwardInfo = packet.CurrentAward;
                self.CurrentSchool = packet.CurrentSchool;

                // Early exit if user is null.
                if (!self.User) {
                    return;
                }

                // Retrieve the list of available schools
                self.appService.GetAvailableSchools(self.AwardInfo.AwardID, self.CurrentSchoolYear).pipe(takeUntil(self._subrelease)).subscribe(schools => {
                    self.AvailableSchools = schools || [];

                    // Retrieve the list of role rights available for designees to use in this
                    // awards system. Also, load the designees for this location.
                    const availRightsObs = self.appService.GetAvailableAwardsRights(self.AwardInfo.AwardID);
                    const designeesObs = self.CurrentSchool ? self.appService.GetDesignees(
                            self.User.AuthToken,
                            self.User.CurrentRole,
                            self.AwardInfo.AwardID,
							(self.CurrentSchool && self.CurrentSchool.SchoolNumber) || self.User.SchoolNumber,
							self.CurrentSchoolYear
                        ) : of<DesigneeInfo[]>([]).pipe(take(1));

                    combineLatest(availRightsObs, designeesObs).pipe(
                        takeUntil(self._subrelease),
                        map(pck => {
                            return {
                                Rights: pck[0],
                                Designees: pck[1],
                            };
                        })
                    ).subscribe(dpacket => {
                        self.AvailableRights = (dpacket.Rights || []).filter(r =>
                            r.CanBeAssigned &&
                            (!r.RequiresDeveloper || self.User.CurrentRole === 'Developer') &&
                            (!r.RequiresSystemAdmin || self.User.CurrentRole === 'Developer' || self.User.CurrentRole === 'SystemAdmin')
                        );

                        // Setup the data source
                        self.NoData = !dpacket.Designees || !dpacket.Designees.length;
                        self.Designees = dpacket.Designees || [];
                        self.DesigneesData.data = self.NoData ? [<DesigneeInfo>{}] : dpacket.Designees;
                        self.DesigneesData.sort = self.sorter;
                        self.DesigneesData.paginator = self.paginator;

                        // Setup the form group. Create a formarray to handle the rights
                        // which could be assigned by this user.
                        self.DesigneeEntryForm = self.fb.group({
                            DesigneeID: [null],
                            FirstName: [null, Validators.required],
                            LastName: [null, Validators.required],
                            LocationNumber: [null, Validators.required],
                            SAPID: [null, Validators.required],
                            PositionNo: [null, Validators.required],
                            Title: [null, Validators.required],
                            UserID: [null, Validators.required],
                            ExpirationDate: [null],
                            Rights: [null],
                        },
                        {
                            validator: function(form) {
                                const formval = form && form.value;

                                if (!formval || !formval.Rights) {
                                    return null;
                                }

                                // At least 1 available rights entry has to be selected for use.
                                let numUsed = 0;

                                for (let right of formval.Rights) {
                                    if (right.Value) {
                                        numUsed++;
                                    }
                                }

                                return numUsed ? null : {
                                    MustUseAtLeastOne: true,
                                };
                            }
                        });

                        const rarr = [];
                        for (let right of self.AvailableRights) {
                            if (
                                right.CanBeAssigned &&
                                (!right.RequiresDeveloper || self.User.CurrentRole === 'Developer') &&
                                (!right.RequiresSystemAdmin ||
                                    self.User.CurrentRole === 'Developer' ||
                                    self.User.CurrentRole === 'SystemAdmin')
                            ) {
                                // This right can be assigned by the current user. Create
                                // a form group entry for it.
                                const rgroup = self.fb.group({
                                    RoleRightsID: [right.RoleRightsID],
                                    Description: [right.Description],
                                    Value: [false],
                                });
                                rarr.push(rgroup);
                            }
                        }

                        self.DesigneeEntryForm.setControl('Rights', self.fb.array(rarr));

                        // Release the view
                        self.ViewReady = true;
                    }, error => self.errorService.HandleServiceError(error));
                }, error => self.errorService.HandleServiceError(error));
            }, error => self.errorService.HandleServiceError(error));
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add Designee Method

    AddDesignee() {
        const self = this;

        const form = self.DesigneeEntryForm.getRawValue();

        if (!form || !self.DesigneeEntryForm.valid) {
            return;
        }

        // Get a confirmation before proceeding
        const msg =
            `You are about to ${form.DesigneeID != null ? 'update' : 'add'} the following ` +
            `${form.DesigneeID != null ? 'new' : ''} designee:<br /><br />` +
            `<strong>Name: </strong>${self.titleCasePipe.transform(form.LastName)}, ` +
            `${self.titleCasePipe.transform(form.FirstName)}<br />` +
            `<strong>SAPID: </strong>${form.SAPID}<br /><strong>Position #: </strong>${form.PositionNo}<br />` +
            `<strong>Title: </strong>${form.Title}<br /><strong>Expiration Date: </strong>` +
            `${
                form.ExpirationDate ? self.dateFnsPipe.transform(form.ExpirationDate, 'M/d/yyyy') : 'None'
            }<br /><strong>Available Rights: </strong><br /><ul>` +
            `${form.Rights.reduce((p, c) => p + (c.Value ? `<li>${c.Description}</li>` : ''), '')}` +
            `</ul><br />Are you sure you wish to continue?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: form.DesigneeID != null ? 'Yes - Update Designee' : 'Yes - Add Designee',
            CancelText: 'No - CANCEL',
        }).pipe(takeUntil(self._subrelease)).subscribe(result => {
            if (result === true) {
                // User has opted to proceed. Signal the system to add/update the designee.

                // Pull the updated rights for this designee.
                const updatedRights: number[] = [];

                for (let right of form.Rights) {
                    if (right.Value) {
                        updatedRights.push(right.RoleRightsID);
                    }
                }

                (form.DesigneeID != null
                    ? // Update operation
                        self.appService.UpdateDesignee(
                            self.User.AuthToken,
                            self.User.CurrentRole,
                            self.AwardInfo.AwardID,
                            form.DesigneeID,
                            form.UserID,
                            form.SAPID,
                            form.PositionNo,
                            form.LocationNumber,
                            form.ExpirationDate,
							updatedRights,
							self.CurrentSchoolYear
                        )
                    : // Add operation
                        self.appService.AddDesignee(
                            self.User.AuthToken,
                            self.User.CurrentRole,
                            self.AwardInfo.AwardID,
                            form.UserID,
                            form.SAPID,
                            form.PositionNo,
                            form.LocationNumber,
                            form.ExpirationDate,
							updatedRights,
							self.CurrentSchoolYear
                        )
                ).subscribe(designees => {
                    // Toast the success
                    self.splashService.SnackbarSuccess(
                        `${form.DesigneeID != null ? 'Designee updated' : 'New designee added'} successfully.`
                    );

                    // Update the list of known designees
                    self.Designees = designees || [];
                    self.NoData = !self.Designees || !self.Designees.length;
                    self.DesigneesData.data = self.NoData ? [<DesigneeInfo>{}] : self.Designees;

                    // Reset the designee form
                    self.CancelDesigneePanel();
                }, error => self.errorService.HandleServiceError(error));
            }
        });
    }

    // #endregion

    // #region Cancel Designee Panel Method

    CancelDesigneePanel() {
        const self = this;

        // Null out the form, then close the panel.
        // Clear the form
        const patch = {
            DesigneeID: null,
            ExpirationDate: null,
            FirstName: null,
            LastName: null,
            LocationNumber: self.CurrentSchool.SchoolNumber,
            SAPID: null,
            PositionNo: null,
            SiteName: null,
            Title: null,
            UserID: null,
        };

        // Patch the values
        self.DesigneeEntryForm.patchValue(patch, { emitEvent: false });

        // Now, iterate all rights, and clear the values to false.
        const rarr = <FormArray>self.DesigneeEntryForm.get('Rights');

        if (rarr && !!rarr.length) {
            for (let i = 0; i < rarr.length; i++) {
                const rentry = <FormGroup>rarr.at(i);
                rentry.get('Value').setValue(false, { emitEvent: false });
            }
        }

        self.ShowingDesigneePanel = false;
    }

    // #endregion

    // #region Remove Designee Method

    RemoveDesignee(designee: DesigneeInfo) {
        const self = this;

        if (!designee) {
            return;
        }

        // Get a confirmation to remove the designee
        const message =
            `You have chosen to remove the following designee:<br /><br />` +
            `<strong>Name: </strong>${self.titleCasePipe.transform(designee.LastName)}, ` +
            `${self.titleCasePipe.transform(designee.FirstName)}<br /><strong>SAPID: </strong>` +
            `${designee.SAPID}<br /><strong>Position #: </strong>${designee.PositionNo}<br />` +
            `<strong>Title: </strong>${designee.Title}<br /><strong>Location: </strong>` +
            `${designee.SiteName} (${designee.LocationNumber})<br />` +
            `<strong>Available Rights: </strong><ul>` +
            `${designee.Rights.reduce(
                (p, c) => p + `<li>${c.Description}</li>`,
                ''
            )}</ul><br />Once removed, this designee will no longer have access to the location ` +
            `provided below in the ${self.AwardInfo.BannerName} system and must be added again in order ` +
            `for he/she to have the <strong>Designee</strong> role and rights access for this location. ` +
            `Are you sure you wish to continue?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: message,
            OKText: 'Yes - Remove',
            CancelText: 'No - CANCEL',
        }).pipe(takeUntil(self._subrelease)).subscribe(result => {
            if (result === true) {
                // User has opted to proceed. Signal the system to remove this designee from the system.
                self.appService.RemoveDesignee(
                    self.User.AuthToken,
                    self.User.CurrentRole,
                    self.AwardInfo.AwardID,
                    designee.DesigneeID,
					self.CurrentSchool.SchoolNumber,
					self.CurrentSchoolYear
                ).pipe(takeUntil(self._subrelease)).subscribe(designees => {
                    // Completed. Refresh the list of designees from what was just returned.
                    // Toast success.
                    this.splashService.SnackbarSuccess('Designee was removed successfully.');

                    // Update the list of known designees
                    self.Designees = designees || [];
                    self.NoData = !self.Designees || !self.Designees.length;
                    self.DesigneesData.data = self.NoData ? [<DesigneeInfo>{}] : self.Designees;

                    return;
                }, error => self.errorService.HandleServiceError(error));
            }
        });
    }

    // #endregion

    // #region Show Designee Panel Method

    ShowDesigneePanel(des?: DesigneeInfo) {
        const self = this;

        // Set up the formgroup to prepare for editing. If we were provided a designee, use those values to prefill the formgroup.
        if (des) {
            const patch = {
                DesigneeID: des.DesigneeID,
                ExpirationDate: des.ExpirationDate,
                FirstName: des.FirstName,
                LastName: des.LastName,
                LocationNumber: des.LocationNumber,
                SAPID: des.SAPID,
                PositionNo: des.PositionNo,
                SiteName: des.SiteName,
                Title: des.Title,
                UserID: des.UserID,
            };

            // Patch the values
            self.DesigneeEntryForm.patchValue(patch);

            // Reset the rights form array entries to match what this designee currently has.
            const farr = <FormArray>self.DesigneeEntryForm.get('Rights');

            for (let i = 0; i < farr.length; i++) {
                const fentry = farr.at(i);
                const rightsID = fentry.get('RoleRightsID').value;

                // Is this in the designee's rights? If so, set the value in the form group
                console.log(des);
                console.log(fentry.get('Description').value);
                console.log(rightsID);
                console.log(des.Rights.some(r => r.RoleRightsID === rightsID && r.RoleActive));
                fentry.get('Value').setValue(des.Rights.some(r => r.RoleRightsID === rightsID && r.RoleActive), { emitEvent: false });
            }
        } else {
            // Clear the form
            const patch = {
                DesigneeID: null,
                ExpirationDate: null,
                FirstName: null,
                LastName: null,
                LocationNumber: self.CurrentSchool.SchoolNumber,
                SAPID: null,
                PositionNo: null,
                SiteName: null,
                Title: null,
                UserID: null,
            };

            // Patch the values
            self.DesigneeEntryForm.patchValue(patch, { emitEvent: false });

            // Now, iterate all rights, and clear the values to false.
            const rarr = <FormArray>self.DesigneeEntryForm.get('Rights');

            if (rarr && !!rarr.length) {
                for (let i = 0; i < rarr.length; i++) {
                    const rentry = <FormGroup>rarr.at(i);
                    rentry.get('Value').setValue(false, { emitEvent: false });
                }
            }
        }

        self.DesigneeEntryForm.updateValueAndValidity({ emitEvent: false });

        self.ShowingDesigneePanel = true;
    }

    // #endregion

    // #region Show Person Lookup

    ShowPersonLookup() {
        const self = this;

        // Pop up the dialog to allow for the user to search for another user using various parameters.
        self.dialogService
            .OpenUserSearchDialog({
                TitleText: 'County Employee User Search',
                MessageText: `Use the following options to search for a Polk County Public Schools user:`,
                ForcedLocationNumber: self.CurrentSchool.SchoolNumber,
				AwardID: self.AwardInfo.AwardID,
				SchoolYear: self.CurrentSchoolYear
            })
            .pipe(takeUntil(self._subrelease)).subscribe(chosen => {
                if (chosen) {
                    // Update the entry form with the values loaded
                    const patch = {
                        UserID: chosen.UserID,
                        SAPID: chosen.SAPID,
                        PositionNo: chosen.PositionNo,
                        FirstName: chosen.FirstName,
                        LastName: chosen.LastName,
                        Title: chosen.LocalJobTitle,
                    };

                    self.DesigneeEntryForm.patchValue(patch);
                }
            });
    }

    // #endregion
}
